/**
 * IconBox Component
 *
 * Reusable gradient icon container used throughout the dashboard.
 * Provides consistent styling for icon boxes with gradient backgrounds.
 */
import React from "react";
import { Box, SxProps, Theme } from "@mui/material";
import { gradients } from "../../utils/theme";

type GradientKey = keyof typeof gradients;

interface IconBoxProps {
  /** Icon element to display */
  icon: React.ReactNode;
  /** Size of the icon box (width & height) */
  size?: number;
  /** Icon size (fontSize) */
  iconSize?: number;
  /** Gradient background from theme */
  gradient?: GradientKey | string;
  /** Border radius */
  borderRadius?: number;
  /** Custom box shadow */
  boxShadow?: string;
  /** Additional sx props */
  sx?: SxProps<Theme>;
}

/**
 * IconBox - Gradient icon container
 *
 * @example
 * ```tsx
 * <IconBox
 *   icon={<DirectionsCar />}
 *   gradient="gold"
 *   size={56}
 * />
 * ```
 */
export const IconBox: React.FC<IconBoxProps> = ({
  icon,
  size = 40,
  iconSize = 20,
  gradient = "royalBlue",
  borderRadius = 2,
  boxShadow,
  sx,
}) => {
  // Get gradient from theme or use custom gradient string
  const gradientValue =
    gradient in gradients ? gradients[gradient as GradientKey] : gradient;

  // Generate shadow based on gradient if not provided
  const shadow =
    boxShadow ||
    (gradient === "royalBlue" || gradient === "navyLux"
      ? "0 8px 16px rgba(26,35,126,0.3)"
      : gradient === "gold"
        ? "0 8px 16px rgba(212,175,55,0.3)"
        : gradient === "emerald"
          ? "0 8px 16px rgba(16,185,129,0.3)"
          : gradient === "deepPurple"
            ? "0 8px 16px rgba(103,58,183,0.3)"
            : "0 8px 16px rgba(0,0,0,0.2)");

  return (
    <Box
      sx={{
        width: size,
        height: size,
        borderRadius,
        background: gradientValue,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        boxShadow: shadow,
        "& > *": {
          color: "white",
          fontSize: iconSize,
        },
        ...sx,
      }}
    >
      {icon}
    </Box>
  );
};

export default IconBox;
